// src/apis/libraryCardApi.js
import { client } from "../apolloClient";
import { GET_LIBRARY_CARD_QUERY } from "../queries/getLibraryCardQuery";
import { GET_GALLERY_QUERY } from "../queries/getGalleryQuery";
import { LIST_LIBRARY_CARDS_QUERY } from "../queries/listLibraryCardsQuery";
import { GET_PRESIGNED_URLS_QUERY } from "../queries/getPresignedUrlsQuery";
import { SAVE_LIBRARY_CARD_MUTATION } from "../mutations/saveLibraryCardMutation";
import { DELETE_ARTWORK_MUTATION } from "../mutations/deleteArtworkMutation";

export const fetchLibraryCardFromApi = async ({ tenantId, entityType, entityId }) => {
  const { data } = await client.query({
    query: GET_LIBRARY_CARD_QUERY,
    variables: { tenantId, entityType, entityId },
    fetchPolicy: "network-only",
  });
  const card = data?.getLibraryCard || null;
  if (!card) return null;
  // Apollo returns frozen objects, so we can't mutate the card in place.
  // Spread into a fresh object and alias `payload` → `data` so code ported
  // verbatim from x20-creative (whose parseCardDetails reads `.data`) works
  // unchanged. Existing consumers that read `.payload` keep working — both
  // fields point to the same string.
  return { ...card, data: card.data ?? card.payload };
};

export const fetchGalleryFromApi = async ({ tenantId }) => {
  const { data } = await client.query({
    query: GET_GALLERY_QUERY,
    variables: { tenantId, entityType: "ARTWORK" },
    fetchPolicy: "network-only",
  });
  return data?.listLibraryCards || [];
};

export const saveLibraryCardToApi = async (input) => {
  const { data } = await client.mutate({
    mutation: SAVE_LIBRARY_CARD_MUTATION,
    variables: { input },
  });
  return data?.saveLibraryCard || null;
};

export const deleteLibraryCardFromApi = async ({ tenantId, entityType, entityId }) => {
  const { data } = await client.mutate({
    mutation: DELETE_ARTWORK_MUTATION,
    variables: { tenantId, entityType, entityId },
  });
  return data?.deleteLibraryCard || null;
};

export const fetchLibraryCardsFromApi = async ({ tenantId, entityType }) => {
  const { data } = await client.query({
    query: GET_GALLERY_QUERY,
    variables: { tenantId, entityType },
    fetchPolicy: "network-only",
  });
  return data?.listLibraryCards || [];
};

// Lightweight list — returns just the tenant's entityIds as `{ pageNames }`.
// peaceful-oasis's AppSync schema doesn't expose a `pageNames` field on the
// LibraryCard type (x20's does), so we select `entityId` per card and
// collapse client-side. Matches the shape x20's listLibraryCardsFromApi
// returns so BlogsPage can be a verbatim port.
export const listLibraryCardsFromApi = async ({ tenantId, entityType }) => {
  const { data } = await client.query({
    query: LIST_LIBRARY_CARDS_QUERY,
    variables: { tenantId, entityType },
    fetchPolicy: "network-only",
  });
  const cards = data?.listLibraryCards || [];
  const pageNames = Array.isArray(cards)
    ? cards.map((c) => c?.entityId).filter(Boolean)
    : [];
  return { pageNames };
};

export const fetchBlogPostsFromApi = async ({ tenantId }) => {
  const { data } = await client.query({
    query: GET_GALLERY_QUERY,
    variables: { tenantId, entityType: "BLOGPOST" },
    fetchPolicy: "network-only",
  });
  return data?.listLibraryCards || [];
};

export const fetchPresignedUrlsFromApi = async (input) => {
  const { data } = await client.query({
    query: GET_PRESIGNED_URLS_QUERY,
    variables: { input },
    fetchPolicy: "network-only",
  });
  return data?.getPresignedUrls?.presignedUrls || [];
};

